import * as React from 'react';
import { Box, Button, Container, Paper, TextField, Grid, Typography, Divider } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import PageDefault from '../../components/PageDefault';
import api from '../../services/api';

export default function CancelCharger() {
    const [codigoVerificador, setCodigoVerificador] = React.useState('');
    const [certidao, setCertidao] = React.useState(false);

    const [carregando, setCarregando] = React.useState(false);

    const { codigo } = useParams();
    const autoRequestedRef = React.useRef(false);

    const navigate = useNavigate();

    const handleSubmit = React.useCallback(() => {

        if (!codigoVerificador) {
            toast.error('Informe o código verificador');
            return;
        }

        setCarregando(true);
        api.get(`/certidao/${codigoVerificador}`)
            .then(async res => {
                setCarregando(false);
                setCertidao(res.data);
            })
            .catch(err => {
                setCarregando(false);
                toast.error(err.response.data.message || 'Ocorreu um erro, verifique os dados');
            })
    }, [codigoVerificador]);

    React.useEffect(() => {
        if (!codigo) return;
        const normalized = String(codigo).trim();
        if (!normalized) return;
        setCodigoVerificador(normalized);
    }, [codigo]);

    React.useEffect(() => {
        if (!codigo || autoRequestedRef.current) return;
        const normalized = String(codigo).trim();
        if (!normalized) return;
        if (codigoVerificador !== normalized) return;

        autoRequestedRef.current = true;
        handleSubmit();
    }, [codigo, codigoVerificador, handleSubmit]);

    const handleBack = () => {
        navigate(-1);
    }

    const formatDateTime = (value) => {
        if (!value) {
            return '-';
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return String(value);
        }
        return new Intl.DateTimeFormat('pt-BR', {
            dateStyle: 'short',
            timeStyle: 'short',
        }).format(date);
    }

    const formatBoolean = (value) => {
        if (value === true) {
            return 'Válida';
        }
        if (value === false) {
            return 'Inválida';
        }
        return '-';
    }

    const formatAssinatura = (value) => {
        if (value === true) {
            return 'Assinatura válida';
        }
        if (value === false) {
            return 'Assinatura inválida';
        }
        return '-';
    }

    const renderDadosValue = (value) => {
        if (value === null || value === undefined) {
            return '-';
        }
        if (typeof value === 'boolean') {
            return value ? 'Sim' : 'Não';
        }
        if (typeof value === 'number' || typeof value === 'string') {
            return String(value);
        }
        try {
            return JSON.stringify(value, null, 2);
        } catch (e) {
            return String(value);
        }
    }

    const renderDados = (dados) => {
        if (!dados || typeof dados !== 'object') {
            return (
                <Typography variant="body2" color="text.secondary">
                    -
                </Typography>
            );
        }

        const isEmptyValue = (value) => {
            if (value === null || value === undefined) return true;
            if (typeof value === 'string' && value.trim() === '') return true;
            if (Array.isArray(value) && value.length === 0) return true;
            if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return true;
            return false;
        };

        const renderSection = (title, entries) => {
            const visible = entries.filter(([, value]) => !isEmptyValue(value));
            if (visible.length === 0) return null;

            return (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }} gutterBottom>
                        {title}
                    </Typography>

                    <Grid container spacing={2}>
                        {visible.map(([label, value]) => (
                            <Grid item xs={12} sm={6} key={label}>
                                <Typography variant="body2" color="text.secondary">
                                    {label}
                                </Typography>

                                {typeof value === 'object' && value !== null ? (
                                    <Box
                                        component="pre"
                                        sx={{
                                            m: 0,
                                            mt: 0.5,
                                            p: 1.5,
                                            borderRadius: 1,
                                            backgroundColor: 'grey.100',
                                            overflowX: 'auto',
                                            fontSize: 12,
                                        }}
                                    >
                                        {renderDadosValue(value)}
                                    </Box>
                                ) : (
                                    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                                        {renderDadosValue(value)}
                                    </Typography>
                                )}
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            );
        };

        const contribuinte = dados.contribuinte && typeof dados.contribuinte === 'object' ? dados.contribuinte : null;
        const imovel = dados.imovel && typeof dados.imovel === 'object' ? dados.imovel : null;

        const certidaoEntries = Object.entries(dados).filter(
            ([key]) => !['targetType', 'targetId', 'contribuinte', 'imovel'].includes(key)
        );

        const labelContribuinte = {
            matricula: 'Matrícula',
            nome: 'Nome',
            cpfCnpj: 'CPF/CNPJ',
        };

        const labelImovel = {
            matricula: 'Matrícula do Imóvel',
            endereco: 'Endereço',
            dataCriacao: 'Data de Registro',
            area: 'Área',
            valorM2: 'Valor do m²',
            quantidadePavimentos: 'Quantidade de pavimentos',
            tipo: 'Tipo',
            natureza: 'Natureza',
            planoDiretor: 'Plano diretor',
            imovelCtn: 'Imóvel CTN',
            titularUnico: 'Titular único',
        };

        const mapWithLabels = (obj, labelMap) => {
            if (!obj) return [];
            return Object.entries(obj).map(([key, value]) => [labelMap[key] || key, value]);
        };

        const contribuinteEntries = mapWithLabels(contribuinte, labelContribuinte);
        const imovelEntries = mapWithLabels(imovel, labelImovel);

        const hasAny = certidaoEntries.length > 0 || contribuinteEntries.length > 0 || imovelEntries.length > 0;
        if (!hasAny) {
            return (
                <Typography variant="body2" color="text.secondary">
                    -
                </Typography>
            );
        }

        return (
            <Box>
                {renderSection('Certidão', certidaoEntries)}
                {renderSection('Contribuinte', contribuinteEntries)}
                {renderSection('Imóvel', imovelEntries)}
            </Box>
        );
    };

    return (
        <PageDefault carregando={carregando} title='Autenticar Certidão'>
            <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>
                <Paper variant="outlined" sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}>
                    <React.Fragment>
                        <Grid container spacing={5} mt={1}>
                            <Grid item xs={12}>
                                <TextField
                                    required
                                    id="numero"
                                    name="numero"
                                    label="Código verificador"
                                    type="text"
                                    fullWidth
                                    value={codigoVerificador}
                                    onChange={e => setCodigoVerificador(e.target.value)}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                onClick={handleBack}
                                sx={{ mt: 3, ml: 1 }}>
                                Voltar
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                sx={{ mt: 3, ml: 1 }}
                            >
                                Verificar
                            </Button>
                        </Box>
                    </React.Fragment>
                </Paper>
                {certidao && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Consulta
                        </Typography>

                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">Código</Typography>
                                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                                    {certidao.codigo || '-'}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">Emitido em</Typography>
                                <Typography variant="body1">
                                    {formatDateTime(certidao.dataCriacao)}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">Expira em</Typography>
                                <Typography variant="body1">
                                    {formatDateTime(certidao.dataExpiracao)}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">Situação</Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 700,
                                        color: certidao.isValida ? 'success.main' : 'error.main',
                                    }}
                                >
                                    {formatBoolean(certidao.isValida)}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">Integridade</Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 700,
                                        color: certidao.assinaturaValida ? 'success.main' : 'error.main',
                                    }}
                                >
                                    {formatAssinatura(certidao.assinaturaValida)}
                                </Typography>
                                {certidao.assinaturaValida === false && (
                                    <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                                        Atenção: os dados retornados pela API não batem com o conteúdo assinado no momento da emissão.
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12} sx={{ mt: 1 }}>
                                <Divider />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }} gutterBottom>
                                    Dados da certidão
                                </Typography>
                                {renderDados(certidao.dados)}
                            </Grid>
                        </Grid>
                    </Paper>
                )}
            </Container>
        </PageDefault>
    );
}